// Check if we're running on the client side
const isClient = typeof window !== "undefined";

/**
 * Get Environment Variable with proper typing
 *
 * @param property Environment Variable Name
 * @param defaultValue Default Value (only used if the env value is undefined)
 * @param env Environment object to use (defaults to the global env)
 */
const getEnv = <TEnv extends Record<string, any>, K extends keyof TEnv>(
  env: TEnv,
  property: K,
  defaultValue?: TEnv[K],
): TEnv[K] => {
  // Only allow client-side variables on client components
  if (isClient && !property.toString().startsWith("NEXT_PUBLIC_")) {
    console.warn(
      `Attempted to access server-side environment variable "${String(property)}" on the client`,
    );
    return defaultValue as TEnv[K];
  }

  const value = env[property];
  return value ?? (defaultValue as TEnv[K]);
};

/**
 * Get Environment Variable or throw an error if it's not defined
 *
 * @param property Environment Variable Name
 * @param env Environment object to use (defaults to the global env)
 */
export const getEnvOrFail = <
  TEnv extends Record<string, any>,
  K extends keyof TEnv,
>(
  env: TEnv,
  property: K,
): NonNullable<TEnv[K]> => {
  const value = env[property];

  if (value === undefined) {
    throw new Error(
      `Missing required environment variable: ${String(property)}`,
    );
  }

  return value as NonNullable<TEnv[K]>;
};

export default getEnv;
